import { setAccountExpiredModalActive } from '@/api';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useAuth } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { accountExpiredState } from '@/state/project';

const DEFAULT_LOGOUT_SECONDS = 20;

/**
 * Non-dismissable modal shown when the current account can no longer be used
 * mid-session — a temporary account expiring (#1054), an admin disabling the
 * account, or an admin deleting it (#1140, which reuses the `disabled`
 * reason). The only action is to log out, which also happens automatically
 * after the backend-provided countdown (20s default). Cannot be closed (no X,
 * no outside-click, no Escape).
 */
export default function AccountExpiredModal() {
  const payload = useRecoilValue(accountExpiredState);
  const { logout } = useAuth();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_LOGOUT_SECONDS);

  useEffect(() => {
    if (!payload) return;
    setAccountExpiredModalActive(true);
    setSecondsLeft(payload.logout_in_seconds ?? DEFAULT_LOGOUT_SECONDS);
    const tick = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          logoutRef.current(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(tick);
      setAccountExpiredModalActive(false);
    };
  }, [payload]);

  if (!payload) return null;

  const heading =
    payload.reason === 'disabled'
      ? 'You no longer have access to this account'
      : 'Your account has expired';

  const reasonLine =
    payload.reason === 'over_quota'
      ? 'This demo account has used its full token allowance.'
      : payload.reason === 'disabled'
        ? 'This account is no longer accessible.'
        : 'This demo account has reached the end of its time limit.';

  const statusLine =
    payload.reason === 'disabled'
      ? 'You will not be able to send further messages or log back in with this account.'
      : 'The account is now in read-only mode — no further messages can be sent. An administrator can re-enable it.';

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-md gap-4 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="space-y-3 text-center">
          <h2 className="text-lg font-semibold">{heading}</h2>
          <p className="text-sm text-muted-foreground">{reasonLine}</p>
          <p className="text-sm text-muted-foreground">{statusLine}</p>
          <p className="text-sm font-medium">
            Thank you for using the Crimson Raven demo.
          </p>
        </div>
        <div className="flex flex-col items-center gap-2">
          <Button className="w-full" onClick={() => logout(true)}>
            Log out
          </Button>
          <span className="text-xs text-muted-foreground">
            You will be logged out automatically in {secondsLeft}s
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
